import { useState, useEffect } from "react";
import { format, addDays } from "date-fns";
import { es } from "date-fns/locale";
import { Sparkles, TrendingUp, Users, Clock, AlertTriangle, ChevronRight, Loader2, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PredictionData {
  targetDate: string;
  targetDayName: string;
  predictedOrders: number;
  predictedRevenue: number;
  avgTicket: number;
  busyHours: string[];
  peakHourOrders: number;
  suggestedStaff: number;
  needsReinforcement: boolean;
  reinforcementShift: string;
  event: { name: string; impactFactor: number } | null;
  confidence: 'alta' | 'media' | 'baja';
  historicalDaysUsed: number;
}

interface PredictionWidgetProps {
  token: string;
}

const PredictionWidget = ({ token }: PredictionWidgetProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [prediction, setPrediction] = useState<PredictionData | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const fetchPrediction = async () => {
    try {
      setIsLoading(true);
      const tomorrow = addDays(new Date(), 1);
      
      const { data, error } = await supabase.functions.invoke("admin-orders", {
        body: { 
          action: "predict-demand", 
          token,
          targetDate: tomorrow.toISOString(),
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Error fetching prediction");

      setPrediction(data.prediction);
    } catch (error) {
      console.error("Error fetching prediction:", error);
      toast({
        title: "Error",
        description: "No se pudo cargar la predicción",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPrediction();
  }, [token]);

  const formatCurrency = (value: number) => {
    return `${value.toFixed(2).replace(".", ",")}€`;
  };

  const getConfidenceBadge = (confidence: string) => {
    switch (confidence) {
      case 'alta':
        return <Badge className="bg-green-500 text-white">Confianza Alta</Badge>;
      case 'media':
        return <Badge className="bg-amber-500 text-white">Confianza Media</Badge>;
      default:
        return <Badge className="bg-red-500 text-white">Datos Limitados</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card className="bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 dark:from-violet-950/30 dark:via-purple-950/30 dark:to-indigo-950/30 border-violet-200 dark:border-violet-800">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
          <span className="ml-2 text-violet-600 dark:text-violet-400">Calculando predicción...</span>
        </CardContent>
      </Card>
    );
  }

  if (!prediction) {
    return null;
  }

  const formattedDate = format(new Date(prediction.targetDate), "EEEE d 'de' MMMM", { locale: es });

  return (
    <Card className="bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 dark:from-violet-950/30 dark:via-purple-950/30 dark:to-indigo-950/30 border-violet-200 dark:border-violet-800 overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
            <Sparkles className="h-5 w-5" />
            <span>Previsión para Mañana</span>
          </div>
          {getConfidenceBadge(prediction.confidence)}
        </CardTitle>
        <p className="text-sm text-violet-600/70 dark:text-violet-400/70 capitalize">
          {formattedDate}
        </p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Main Predictions */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white/60 dark:bg-white/5 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-violet-700 dark:text-violet-300">
              ~{formatCurrency(prediction.predictedRevenue)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Ventas estimadas</div>
          </div>
          <div className="bg-white/60 dark:bg-white/5 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-violet-700 dark:text-violet-300">
              ~{prediction.predictedOrders}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Pedidos esperados</div>
          </div>
        </div>

        {/* Staff Alert */}
        {prediction.needsReinforcement && (
          <div className="flex items-center gap-2 p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg border border-amber-300 dark:border-amber-700">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <span className="text-sm text-amber-800 dark:text-amber-200">
              🔥 Refuerzo necesario en turno de <strong>{prediction.reinforcementShift}</strong>
            </span>
          </div>
        )}

        {/* Event Impact */}
        {prediction.event && (
          <div className="flex items-center gap-2 p-3 bg-pink-100 dark:bg-pink-900/30 rounded-lg border border-pink-300 dark:border-pink-700">
            <Calendar className="h-5 w-5 text-pink-600 dark:text-pink-400 flex-shrink-0" />
            <span className="text-sm text-pink-800 dark:text-pink-200">
              Impacto por: <strong>{prediction.event.name}</strong> (×{prediction.event.impactFactor})
            </span>
          </div>
        )}

        {/* Quick Stats Row */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Horas punta: {prediction.busyHours.join(', ') || 'N/D'}</span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>{prediction.suggestedStaff} persona(s)</span>
          </div>
        </div>

        {/* Detail Button */}
        <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DialogTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-violet-600 hover:text-violet-700 hover:bg-violet-100 dark:text-violet-400 dark:hover:bg-violet-900/30"
            >
              Ver previsión detallada
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-500" />
                Previsión Detallada
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Date Info */}
              <div className="text-center pb-4 border-b">
                <p className="text-lg font-semibold capitalize">{formattedDate}</p>
                {getConfidenceBadge(prediction.confidence)}
              </div>

              {/* Detailed Metrics */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-primary">
                    ~{formatCurrency(prediction.predictedRevenue)}
                  </div>
                  <div className="text-xs text-muted-foreground">Facturación</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-primary">
                    ~{prediction.predictedOrders}
                  </div>
                  <div className="text-xs text-muted-foreground">Pedidos</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-primary">
                    {formatCurrency(prediction.avgTicket)}
                  </div>
                  <div className="text-xs text-muted-foreground">Ticket Medio</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-primary">
                    {prediction.suggestedStaff}
                  </div>
                  <div className="text-xs text-muted-foreground">Personal Sugerido</div>
                </div>
              </div>

              {/* Busy Hours */}
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Horas Punta Previstas
                </h4>
                <div className="flex gap-2 flex-wrap">
                  {prediction.busyHours.length > 0 ? (
                    prediction.busyHours.map((hour, index) => (
                      <Badge key={hour} variant={index === 0 ? "default" : "secondary"}>
                        {hour} {index === 0 && "🔥"}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">Sin datos suficientes</span>
                  )}
                </div>
              </div>

              {/* Peak Hour Detail */}
              <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Pedidos en hora punta:</span>
                  <span className="font-bold">~{prediction.peakHourOrders}/hora</span>
                </div>
              </div>

              {/* Event */}
              {prediction.event && (
                <div className="bg-pink-50 dark:bg-pink-950/20 rounded-lg p-3">
                  <h4 className="text-sm font-medium mb-1">📅 Evento Especial</h4>
                  <p className="text-sm">
                    <strong>{prediction.event.name}</strong> - Factor de impacto: ×{prediction.event.impactFactor}
                  </p>
                </div>
              )}

              {/* Historical Data Info */}
              <div className="text-xs text-muted-foreground text-center pt-2 border-t">
                Predicción basada en {prediction.historicalDaysUsed} {prediction.targetDayName}s anteriores
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default PredictionWidget;
